import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type ReportScheduleSettingRecord from 'webapp/models/report-schedule-setting';

export default class ReportScheduleSettingSerializer extends ApplicationSerializer {
	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.ReportScheduleSetting.Raw | RecordData.ReportScheduleSetting.Serialized,
		prop: string | undefined,
	) {
		const { schedule, ...data } = json;
		// scheduleUser can be null before the object transform turns it into {}
		const attributes: Partial<
			Omit<RecordAttributes<ReportScheduleSettingRecord>, 'scheduleUser'>
		> & {
			id?: string | number;
			scheduleUser?: ReportScheduleSettingRecord['scheduleUser'] | null;
		} = { ...data };

		if (schedule) {
			attributes.scheduleType = schedule.type;
			attributes.scheduleHourOfDay = schedule.hourOfDay;
			attributes.scheduleDayOfWeek = schedule.dayOfWeek || [];
			attributes.scheduleDayOfMonth = schedule.dayOfMonth;
			attributes.scheduleUser = 'user' in schedule ? schedule.user : undefined;
		} else {
			attributes.scheduleType = 0;
			attributes.scheduleUser = null;
		}

		// "generate now, send later" reports are not scheduled
		if (attributes.scheduledOn) {
			attributes.scheduleType = 0;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.ReportScheduleSetting.Serialized {
		const attributes = super.serialize(
			snapshot,
			options,
		) as RecordAttributes<ReportScheduleSettingRecord>;

		const json: RecordData.ReportScheduleSetting.Serialized = omit(
			attributes,
			'scheduleType',
			'scheduleHourOfDay',
			'scheduleDayOfWeek',
			'scheduleDayOfMonth',
			'scheduleUser',
		);

		if (attributes.scheduleType !== 0) {
			json.schedule = {
				type: attributes.scheduleType,
				hourOfDay: attributes.scheduleHourOfDay,
				dayOfWeek: attributes.scheduleDayOfWeek,
				dayOfMonth: attributes.scheduleDayOfMonth,
			};
		}

		return json;
	}
}
